// src/pages/master/ClientForm.js
// FIXED: sites now written to top-level `sites` collection (SOT),
// never to client.sites[] embedded array (deprecated per D-154/Section 83)
// Premium light UI — teal/slate theme

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, addDoc, getDocs, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

const EMPTY_SITE = { site_name: "", address: "", location: "", map_link: "", lat: null, lng: null };

export default function ClientForm() {
  const navigate   = useNavigate();
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "wFn3Lgu6GuTWIVZ5qb8u";

  const [form, setForm] = useState({ client_name: "", contact: "", gstin: "" });
  const [sites, setSites] = useState([{ ...EMPTY_SITE }]);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) =>
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSiteChange = (index, e) => {
    const updated = [...sites];
    updated[index][e.target.name] = e.target.value;
    setSites(updated);
  };

  const addSite = () => setSites(prev => [...prev, { ...EMPTY_SITE }]);

  const removeSite = (index) =>
    setSites(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name.trim()) { alert("Client name required"); return; }
    if (!sites[0].site_name.trim()) { alert("At least one site name required"); return; }

    // Duplicate site check
    const names = sites.map(s => s.site_name.toLowerCase().trim());
    if (new Set(names).size !== names.length) {
      alert("Duplicate site names not allowed"); return;
    }

    try {
      setSaving(true);

      // Check duplicate client (same name + company)
      const dupSnap = await getDocs(query(
        collection(db, "clients"),
        where("company_id",   "==", company_id),
        where("client_name",  "==", form.client_name.trim())
      ));

      if (!dupSnap.empty) {
        alert(`Client "${form.client_name}" already exists. Use Edit Client to add sites.`);
        setSaving(false);
        return;
      }

      // ── STEP 1: Create client — WITHOUT embedded sites[] array (SOT fix) ──
      const clientRef = await addDoc(collection(db, "clients"), {
        company_id,
        client_name: form.client_name.trim(),
        contact:     form.contact.trim(),
        gstin:       form.gstin.trim().toUpperCase(), // GST-registered clients only — optional field, blank if client is not GST-registered
        created_at:  serverTimestamp(),
        created_by:  user.uid || user.id || "",
        created_by_name: user.name || "",
      });

      // ── STEP 2: Create each site as its own doc in the sites collection ──
      // (SOT — same schema/pattern as QuickCreateSite.jsx / EditClient.js)
      const siteWrites = sites.map((s) => {
        // Fold the extra "location" text field into address if address is
        // blank, so this data isn't silently dropped — sites collection's
        // canonical schema has no separate "location" field.
        const combinedAddress = s.address.trim() ||
          (s.location.trim() ? s.location.trim() : "");

        return addDoc(collection(db, "sites"), {
          company_id,
          client_id:       clientRef.id,
          client_name:     form.client_name.trim(),
          site_name:       s.site_name.trim(),
          address:         combinedAddress,
          location_link:   s.map_link.trim() ||
            (s.lat && s.lng ? `https://www.google.com/maps?q=${s.lat},${s.lng}` : ""),
          lat:             s.lat || null,
          lng:             s.lng || null,
          created_at:      serverTimestamp(),
          created_by:      user.uid || user.id || "",
          created_by_name: user.name || "",
        });
      });

      await Promise.all(siteWrites);

      setSuccess(true);
      setTimeout(() => navigate("/master/client-list"), 1000);
    } catch (err) {
      console.error(err);
      alert("Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Client Master</h1>
          <p className="text-sm text-slate-500 mt-0.5">Add new client with sites</p>
        </div>
        <button onClick={() => navigate("/master/client-list")}
          className="text-sm text-slate-500 hover:text-slate-700">
          View All Clients →
        </button>
      </div>

      {success && (
        <div className="px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 font-semibold">
          ✅ Client saved successfully! Redirecting...
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Client Details */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
              👤 Client Details
            </h2>
          </div>
          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1.5">
                Client Name *
              </label>
              <input type="text" name="client_name"
                value={form.client_name} onChange={handleChange}
                placeholder="e.g. DLF Pvt Ltd"
                required
                className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1.5">
                Contact Number
              </label>
              <input type="text" name="contact"
                value={form.contact} onChange={handleChange}
                placeholder="e.g. 9876543210"
                className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1.5">
                Client GSTIN <span className="text-slate-400 font-normal normal-case">(if GST-registered)</span>
              </label>
              <input type="text" name="gstin"
                value={form.gstin} onChange={handleChange}
                placeholder="e.g. 05ACDFA5998G1Z6"
                maxLength={15}
                className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400 transition uppercase" />
            </div>
          </div>
        </div>

        {/* Sites */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
            <h2 className="text-sm font-bold text-slate-700 uppercase tracking-wide">
              📍 Site Details
            </h2>
            <span className="text-xs text-slate-400">{sites.length} site{sites.length !== 1 ? "s" : ""}</span>
          </div>

          <div className="p-5 space-y-4">
            {sites.map((site, index) => (
              <div key={index}
                className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-bold text-teal-600 uppercase tracking-wide">
                    Site {index + 1}
                  </span>
                  {sites.length > 1 && (
                    <button type="button" onClick={() => removeSite(index)}
                      className="text-xs text-red-500 hover:text-red-700 font-semibold">
                      ✕ Remove
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Site Name *</label>
                    <input name="site_name" value={site.site_name}
                      onChange={e => handleSiteChange(index, e)}
                      placeholder="e.g. DLF Mall Ground Floor"
                      required
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-teal-400 bg-white transition" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Location</label>
                    <input name="location" value={site.location}
                      onChange={e => handleSiteChange(index, e)}
                      placeholder="e.g. Gurugram, Haryana"
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-teal-400 bg-white transition" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Address</label>
                    <input name="address" value={site.address}
                      onChange={e => handleSiteChange(index, e)}
                      placeholder="Full address"
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-teal-400 bg-white transition" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Google Map Link</label>
                    <input name="map_link" value={site.map_link}
                      onChange={e => handleSiteChange(index, e)}
                      placeholder="https://maps.google.com/..."
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-teal-400 bg-white transition" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs font-semibold text-slate-500 block mb-1">GPS Coordinates</label>
                    <div className="flex gap-2 items-center">
                      <input readOnly
                        value={site.lat && site.lng ? `${site.lat}, ${site.lng}` : ""}
                        placeholder="Capture GPS location at site"
                        className="flex-1 border border-slate-200 rounded-xl p-3 text-sm bg-slate-50 text-slate-500" />
                      <button type="button"
                        onClick={() => {
                          if (!navigator.geolocation) { alert("GPS not supported"); return; }
                          navigator.geolocation.getCurrentPosition(
                            pos => {
                              const updated = [...sites];
                              updated[index].lat = pos.coords.latitude;
                              updated[index].lng = pos.coords.longitude;
                              updated[index].map_link = updated[index].map_link ||
                                `https://www.google.com/maps?q=${pos.coords.latitude},${pos.coords.longitude}`;
                              setSites(updated);
                            },
                            () => alert("Location permission denied")
                          );
                        }}
                        className="px-4 py-3 bg-teal-600 text-white rounded-xl text-sm font-semibold whitespace-nowrap hover:bg-teal-700">
                        📍 Capture GPS
                      </button>
                    </div>
                    {site.lat && site.lng && (
                      <p className="text-xs text-teal-600 mt-1 font-semibold">✅ GPS saved — {site.lat.toFixed(5)}, {site.lng.toFixed(5)}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}

            <button type="button" onClick={addSite}
              className="w-full py-3 border-2 border-dashed border-teal-300 text-teal-600 rounded-xl text-sm font-semibold hover:bg-teal-50 transition">
              + Add Another Site
            </button>
          </div>
        </div>

        {/* Submit */}
        <button type="submit" disabled={saving}
          className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold text-sm disabled:opacity-50 transition shadow-sm">
          {saving ? "Saving..." : "💾 Save Client"}
        </button>

      </form>
    </div>
  );
}